import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { toast } from 'react-toastify';
import { fetchVerifiedOrders, Order } from '../api/api'; 
import 'react-toastify/dist/ReactToastify.css';
import { FaEdit } from 'react-icons/fa'; 
import { useNavigate } from 'react-router-dom';

const VerifiedOrders: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchVerifiedOrders();
            setOrders(response.data);
        } catch (err) {
            setError('Failed to fetch verified orders.');
            toast.error('Failed to fetch verified orders.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const handleUpdate = (orderId: string) => {
        navigate(`/admin/update-order/${orderId}`);
    };

    if (loading) return <AdminLayout><div className="p-6 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 min-h-screen">Loading Orders...</div></AdminLayout>;
    if (error) return <AdminLayout><div className="p-6 text-red-500 bg-gray-50 dark:bg-gray-900 min-h-screen">Error: {error}</div></AdminLayout>;

    return (
        <AdminLayout>
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">All Verified Orders</h1>
                
                {orders.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400">No verified orders found.</p>
                ) : (
                    <div className="overflow-x-auto custom-scrollbar bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Order ID</th>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Customer</th>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Product Details</th>
                                    <th className="py-3 px-4 text-right text-xs text-gray-500 dark:text-gray-300">Total Payable</th>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Seller</th>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Status</th>
                                    <th className="py-3 px-4 text-center text-xs text-gray-500 dark:text-gray-300">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {orders.map((order) => {
                                    const cName = order.mainUser ? `${order.mainUser.firstName} ${order.mainUser.lastName || ''}` : (order.customerName || 'Unknown');
                                    const cMobile = order.mainUser?.mobile || order.mobile || 'N/A';
                                    
                                    return (
                                        <tr key={order.id} className="text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="py-3 px-4 font-mono text-sm text-gray-600 dark:text-gray-400">
                                                {order.orderId || order.id.substring(0, 8)}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className='font-semibold text-gray-900 dark:text-white'>{cName}</div>
                                                <div className='text-xs text-gray-500 dark:text-gray-400'>{cMobile}</div>
                                            </td>
                                            <td className="py-3 px-4 text-sm">
                                                <div className='flex flex-col'>
                                                    <span className="text-gray-900 dark:text-white font-medium">{order.medicine}</span>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-2">
                                                        <span>Price: {order.medicinePrice}</span>
                                                        <span>Qty: x{order.medicineQuantity}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-right text-lg font-semibold text-green-600 dark:text-green-400">
                                                ₹{order.payableAmount?.toFixed(2)}
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{order.sellerUsername || 'N/A'}</td>
                                            <td className="py-3 px-4">
                                                <span className="bg-green-100 dark:bg-green-500 text-green-800 dark:text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <div className="flex justify-center">
                                                    <button
                                                        onClick={() => handleUpdate(order.id)}
                                                        className="p-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white shadow-sm transition-colors"
                                                        title="Edit/Update Order"
                                                    >
                                                        <FaEdit className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f3f4f6; }
                .dark .custom-scrollbar::-webkit-scrollbar-track { background: #1f2937; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #4b5563; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #6b7280; }
            `}</style>
        </AdminLayout>
    );
};

export default VerifiedOrders;